import styled, { css } from 'styled-components';
import { transparentize } from 'utils/color';
import media from 'components/mixins/media';
import * as typography from 'components/mixins/typography';
import Icon from 'components/common/Icon';
import { centerAbsoluteElement } from 'components/mixins/placement';
import { defaultBoxShadowColor } from 'constants/common';

export const PlusIcon = styled(Icon)`
  position: absolute;
  ${centerAbsoluteElement};
  text-indent: 0;
  color: ${props => props.theme.colors.mainColor};
`;

export const mainColor = `${props => props.theme.colors.mainColor}`;

export const Helper = styled.div`
  display: inline-flex;

  ${media.xsmall`
    display: block;
    line-height: 40px;
  `};

  .dropdown {
    display: block;
    line-height: 40px;
    max-height: 40px;
    outline: none;
    position: relative;

    ${props =>
      props.isMatching &&
      media.xsmall`
        max-height: 30px;
        line-height: 30px;
      `};

    ${props =>
      props.isMatching &&
      css`
        .dropdown-button[aria-expanded='true'] {
          background: ${props.theme.colors.mainColor};
          vertical-align: top;

          &:before {
            display: none;
          }

          ${PlusIcon} {
            color: ${props.theme.colors.contentBodyColor};
          }
        }
      `};
  }

  .dropdown-button {
    vertical-align: top;
    position: relative;
    display: inline-block;
    max-width: 280px;
    min-width: 210px;
    max-height: 40px;
    margin: 0 8px;
    padding: 0 30px 0 14px;
    border: none;
    border-radius: 2px;
    background-color: ${props => props.theme.colors.contentBodyColor};
    box-shadow: 0 2px 10px 0 ${transparentize(defaultBoxShadowColor, 0.1)};
    color: ${props => props.theme.colors.textColor};
    ${typography.mainFontSmallText};
    text-align: left;
    line-height: 40px;
    text-overflow: ellipsis;
    overflow: hidden;
    outline: none;
    word-break: break-all;
    white-space: nowrap;
    transition: all 0.1s ease-out;
    cursor: pointer;

    &::-moz-focus-inner {
      outline: none;
      border: 0;
    }

    .dropdown-arrow {
      content: '';
      position: absolute;
      top: 16px;
      right: 15px;
      width: 6px;
      height: 6px;
      color: ${props => props.theme.colors.textColor};
      border-style: solid;
      border-width: 2px 2px 0 0;
      vertical-align: top;
      transform: rotate(135deg);
      transition: all 0.1s ease-out;

      &.is-open {
        top: 18px;
        transform: rotate(-45deg);
      }
    }

    &.empty {
      color: ${props => transparentize(props.theme.colors.textColor, 0.5)};
    }

    &:hover {
      box-shadow: 0 4px 8px 0 ${transparentize(defaultBoxShadowColor, 0.1)};
    }
    &:focus {
      box-shadow: 0 1px 0 0 ${transparentize(defaultBoxShadowColor, 0.1)},
        inset 0 1px 3px 0 ${transparentize(defaultBoxShadowColor, 0.1)};
    }

    ${props =>
      props.isMatching
        ? css`
            overflow: visible;
            max-width: auto;
            min-width: auto;
            max-height: none;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            padding: 0;
            background: ${props.theme.colors.contentBodyColor};
            text-indent: -999999px;
            z-index: 1;

            &:before {
              content: '';
              display: block;
              position: absolute;
              top: -3px;
              left: -3px;
              height: 36px;
              width: 36px;
              border-radius: 50%;
              transition: 0.3s;
              border: 3px solid ${transparentize(props.theme.colors.contentBodyColor, 0.5)};
            }

            &:after {
              content: '';
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;
              background: transparent;
              transition: 0.3s;
              border-radius: 50%;
            }

            &:hover {
              background: ${transparentize(props.theme.colors.contentBodyColor, 0.9)};

              &:after {
                background: ${transparentize(props.theme.colors.mainColor, 0.25)};
              }

              &:before {
                top: -11px;
                left: -11px;
                border: 11px solid ${transparentize(props.theme.colors.contentBodyColor, 0.2)};
              }
            }

            &:focus {
              background: ${transparentize(props.theme.colors.contentBodyColor, 0.9)};

              &:after {
                background: ${transparentize(props.theme.colors.mainColor, 0.25)};
              }
            }

            .dropdown-arrow {
              display: none;
              top: 50%;
              left: 50%;
              margin-top: -3px;
              margin-left: -3px;
              color: ${props.theme.colors.mainColor};

              &.is-open {
                top: 50%;
                margin-top: -2px;
              }
            }

            & + .chosen-value {
              background: ${props.theme.colors.contentBodyColor};
              position: absolute;
              top: 0;
              left: 45px;
              padding: 4px 30px 3px 16px;
              line-height: 32px;
              border-radius: 2px;
              ${typography.mainFontText};
              white-space: nowrap;
              max-width: 340px;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            ${media.xsmall`
              width: 30px;
              height: 30px;

              &:before {
                height: 30px;
                width: 30px;
              }

              ${PlusIcon} {
                font-size: 8px;
              }

              & + .chosen-value {
                left: 39px;
              }
            `};
          `
        : css``};
  }

  ${media.xsmall`
    max-width: 100%;
    width: 100%;
    padding: ${props => (props.isMatching ? '0' : '0 30px 0 14px')};
  `};
`;

export const PopoverContent = styled.div`
  position: relative;
  ${props =>
    props.isMatching
      ? css`
          &:before {
            content: '';
            display: block;
            position: absolute;
            left: 5px;
            right: 5px;
            bottom: 0;
            height: 16px;
            z-index: 11;
            background: linear-gradient(
              to top,
              ${props.theme.colors.contentBodyColor},
              ${transparentize(props.theme.colors.contentBodyColor, 0)}
            );
          }
        `
      : css``};

  .downshift-dropdown {
    position: static;
    width: auto;
    min-width: 210px;
    max-width: 340px;
    max-height: 360px;
    margin: 10px 8px;
    padding: 10px 0;
    background-color: ${props => props.theme.colors.contentBodyColor};
    border: none;
    border-radius: 2px;
    box-shadow: 0 2px 10px 0 ${transparentize(defaultBoxShadowColor, 0.1)};
    box-sizing: border-box;
    color: ${props => props.theme.colors.textColor};
    overflow-y: auto;
    z-index: 10;
    -webkit-overflow-scrolling: touch;
    transition: all 0.3s ease-out;

    ${media.xsmall`
      max-height: 200px;
      max-width: 250px;
    `};

    ${props =>
      props.isMatching
        ? css`
            ${media.small`
              max-width: 40vw;
            `};
            ${media.mobile`
              max-height: 90vh;
            `};
            ${media.xsmall`
              max-height: 200px;
              max-width: 210px;
            `};
            transition: none;
            margin: 5px;
          `
        : css``};
  }

  .dropdown-item {
    box-sizing: border-box;
    color: ${props => props.theme.colors.textColor};
    cursor: pointer;
    display: block;
    padding: 12px 24px;
    white-space: normal;
    ${typography.mainFontSmallText};
    line-height: 22px;

    &.main-color {
      color: ${props => props.theme.colors.mainColor};
    }

    &.selected {
      color: ${props => transparentize(props.theme.colors.textColor, 0.5)};
      text-decoration: line-through;
    }

    &.current-selected {
      color: ${props => props.theme.colors.mainColor};
    }

    &:hover {
      color: ${props => props.theme.colors.mainColor};
      background-color: ${props => transparentize(props.theme.colors.textColor, 0.05)};
      margin: auto 3px;
      padding: 12px 21px;

      &.selected {
        color: ${props => transparentize(props.theme.colors.textColor, 0.5)};
      }
    }

    &:focus {
      color: ${props => props.theme.colors.mainColor};
      background-color: ${props => transparentize(props.theme.colors.textColor, 0.05)};
    }
  }
`;

export const PopoverButton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
`;
